'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Label, Select, Spinner, TextInput } from 'flowbite-react';
import { toast } from 'react-toastify';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useAppSelector } from '../../lib/hooks.ts';
import {
    useDeleteCompanyMutation,
    useGetAllBuildingsQuery,
    useGetBedrijfByNameQuery,
    useGetCompanyDeleteImpactQuery,
    useUpdateCompanyMutation,
} from '../../lib/model/kantoordagApi.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';

interface CompanyConfigFormProps {
    name: string;
    onSaved: () => void;
    onDeleted: () => void;
}

// The caller renders this with key={name} (CompanyAdmin.tsx) so switching to a different
// company remounts it, resetting the form fields below back to "not yet edited" -- no
// effect-based reset needed.
export default function CompanyConfigForm({ name, onSaved, onDeleted }: CompanyConfigFormProps) {
    const { t } = useTranslation('company-admin');
    const user = useAppSelector(state => state.user.user);
    // Deleting a company can't be undone and DELETE /bedrijf/{name} requires
    // Roles.SUPER_ADMINISTRATION (BedrijfResource.kt) -- ADMIN alone is refused by the backend,
    // so hide the trigger here too rather than let a plain admin hit a 403 after confirming.
    const canDeleteCompany = userHasRole(user, ['SUPER_ADMIN']);

    const { data: company, isLoading, isError, refetch } = useGetBedrijfByNameQuery({ name });
    const { data: buildings } = useGetAllBuildingsQuery();
    // Only fetched for a SUPER_ADMIN: GET /bedrijf/{name}/delete-impact requires the same role as
    // the delete itself, so a plain admin would just get a 403 here.
    const { data: deleteImpact } = useGetCompanyDeleteImpactQuery({ name }, { skip: !canDeleteCompany });
    const [updateCompany, updateCompanyResult] = useUpdateCompanyMutation();
    const [deleteCompany, deleteCompanyResult] = useDeleteCompanyMutation();

    // Undefined means "not yet edited by the user" -- falls back to the fetched company's value
    // below, so the fields populate once `company` loads without needing an effect-driven
    // setState (the caller remounts this component per company via key={name}, so a real
    // company switch always starts these back at undefined).
    const [domainInput, setDomainInput] = useState<string | undefined>(undefined);
    const [logoInput, setLogoInput] = useState<string | undefined>(undefined);
    const [buildingInput, setBuildingInput] = useState<string | undefined>(undefined);
    const [domainError, setDomainError] = useState<string | undefined>(undefined);
    const [buildingError, setBuildingError] = useState<string | undefined>(undefined);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const domain = domainInput ?? company?.domain ?? '';
    const logo = logoInput ?? company?.logo ?? '';
    const building = buildingInput ?? company?.building ?? '';

    function save() {
        setDomainError(undefined);
        setBuildingError(undefined);

        const trimmedDomain = domain.trim();
        if (trimmedDomain === '') {
            setDomainError(t('company_domain_required'));
            return;
        }

        updateCompany({
            name,
            companyWrite: {
                domain: trimmedDomain,
                logo: logo.trim(),
                building,
            },
        })
            .unwrap()
            .then(() => {
                toast.success(t('company_saved'));
                // Shares its cache entry with CompanyRow's own useGetBedrijfByNameQuery({ name })
                // (same endpoint + args), so refetching here also refreshes the list row.
                refetch();
                onSaved();
            })
            .catch((error) => {
                // PUT /bedrijf/{name}'s WebApplicationException(message, status) never sets a
                // response body, so a 409 (domain already used by a different company) can't be
                // told apart from anything else by message -- surface it next to the domain field,
                // mirroring CreateCompanyDialog.tsx. A 404 here means the building wasn't found
                // (the company itself existing is a precondition of this form being open at all).
                if (error?.status === 409) {
                    setDomainError(t('company_update_conflict'));
                } else if (error?.status === 404) {
                    setBuildingError(t('company_building_not_found'));
                } else {
                    toastError({ api: updateCompanyResult, message: t('company_save_failed') });
                }
            });
    }

    function confirmDelete() {
        setShowDeleteConfirm(false);
        deleteCompany({ name })
            .unwrap()
            .then(() => {
                toast.success(t('company_deleted'));
                onDeleted();
            })
            .catch(() => toastError({ api: deleteCompanyResult, message: t('company_delete_failed') }));
    }

    if (isLoading) {
        return <Spinner/>;
    }
    if (isError || company == null) {
        return <p className="text-red-600">{t('loading_failed')}</p>;
    }

    return (
        <div data-testid="company-config-form" className="flex max-w-md flex-col gap-4">
            <h2 className="text-lg font-semibold">{t('config_title')}</h2>

            <div>
                <Label htmlFor="company-domain">{t('company_domain')}</Label>
                <TextInput
                    id="company-domain"
                    value={domain}
                    color={domainError != null ? 'failure' : undefined}
                    onChange={event => setDomainInput(event.target.value)}
                />
                {domainError != null && <p className="mt-1 text-sm text-red-600">{domainError}</p>}
            </div>

            <div>
                <Label htmlFor="company-logo">{t('company_logo')}</Label>
                <TextInput
                    id="company-logo"
                    value={logo}
                    onChange={event => setLogoInput(event.target.value)}
                />
            </div>

            <div>
                <Label htmlFor="company-building">{t('company_building')}</Label>
                <Select
                    id="company-building"
                    value={building}
                    color={buildingError != null ? 'failure' : undefined}
                    onChange={event => setBuildingInput(event.target.value)}
                >
                    {buildings?.map(b => (
                        <option key={b.name} value={b.name}>{b.name}</option>
                    ))}
                </Select>
                {buildingError != null && <p className="mt-1 text-sm text-red-600">{buildingError}</p>}
            </div>

            <div className="flex gap-2">
                <Button onClick={save} disabled={updateCompanyResult.isLoading}>
                    {t('save')}
                </Button>
                {canDeleteCompany && (
                    <Button
                        id="company-delete"
                        color="failure"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleteCompanyResult.isLoading}
                    >
                        {t('delete_company')}
                    </Button>
                )}
            </div>

            <ConfirmDialog
                message={t('confirm_delete_company', { company: company.naam, count: deleteImpact?.matchingUserCount ?? 0 })}
                yes={t('confirm_yes')}
                no={t('confirm_no')}
                show={showDeleteConfirm}
                onYes={confirmDelete}
                onNo={() => setShowDeleteConfirm(false)}
            />
        </div>
    );
}
